import { API } from 'data';
import { __PostsFiltersType, __PostSearchParamsType } from './posts.type';
import { RestAPI } from 'scripts';
import { __MergePaginationPage } from 'scripts/paginationQuery';
import { CategoryHierarchyType, CategoryType, LocationType, PaginationType, PostType } from 'types';

export { __PostsPageURL } from './posts.url';
export const __POSTS_PAGE_SIZE = 36;
export const __POSTS_LIST_SESSION_KEY = ['posts', 'list', 'session'] as const;

export const __PostsQueryKey = (queryBaseKey: string) => ['posts', 'list', queryBaseKey] as const;

/** Filter fields only — used for react-query cache keys and session (excludes locations; those are read from context at fetch time). */
export function __PostsFilterQueryBase(filters: __PostsFiltersType): {
    categoryFields: { id: number; value: number }[];
    ranges: { id: number; value: [number, number] }[];
    search: string;
    category: string;
} {
    return {
        categoryFields: [...(filters.categoryFields ?? [])]
            .sort((a, b) => a.id - b.id)
            .map((i) => ({ id: i.id, value: i.value })),
        ranges: [...(filters.ranges ?? [])].sort((a, b) => a.id - b.id).map((i) => ({ id: i.id, value: i.value })),
        search: filters.search ?? '',
        category: filters.category?.slug ?? '',
    };
}

export const __PostsFilterQueryKey = (filters: __PostsFiltersType) => JSON.stringify(__PostsFilterQueryBase(filters));

/** True when the current list URL reflects the same filters as the cached session (e.g. browser back). */
export const __PostsSessionMatchesUrl = (filters: __PostsFiltersType): boolean => {
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    if (filters.category?.slug && url.searchParams.get('category') !== filters.category.slug) return false;
    if (!filters.category?.slug && url.searchParams.get('category')) return false;
    if (filters.search && url.searchParams.get('search') !== filters.search) return false;
    if (!filters.search && url.searchParams.get('search')) return false;
    for (const field of filters.categoryFields ?? []) {
        const values = url.searchParams.getAll(`category_field[${field.id}]`);
        if (!values.includes(field.value.toString())) return false;
    }
    for (const range of filters.ranges ?? []) {
        const values = url.searchParams.getAll(`range_category_field[${range.id}]`);
        const serialized = range.value.join(',');
        if (!values.some((v) => v === serialized || v.split(',').join(',') === serialized)) return false;
    }
    return true;
};

export function __FetchPosts(
    filters: __PostsFiltersType,
    locations?: LocationType<'list'>[],
    prev?: PaginationType<PostType<'list'>>,
): Promise<PaginationType<PostType<'list'>>> {
    const page = filters.page ?? 1;
    return RestAPI.get<PaginationType<PostType<'list'>>>(__PostFetchURL(filters, locations)).then((res) =>
        __MergePaginationPage(page, res.data, prev),
    );
}

export const __GetParentCategories = (categories?: CategoryHierarchyType[]): CategoryType<'list'>[] => {
    if (!categories) return [];
    return categories.map((item) => ({
        id: item.id,
        title: item.title,
        slug: item.slug,
        image: item.image,
        price: item.price,
        parent: item.parent,
        icon: item.icon,
        renewed_price: item.renewed_price,
    }));
};

const extractListParams = (e: object): Pick<__PostsFiltersType, 'categoryFields' | 'ranges'> => {
    const categoryFields: { id: number; value: number }[] = [];
    const ranges: { id: number; value: [number, number] }[] = [];
    Object.entries(e).map((item) => {
        // ^.*?range_category_field\[[^\d]*(\d+)[^\d]*\].*$
        if (/^.*?\[[^\d]*(\d+)[^\d]*\].*$/.test(item[0])) {
            // search param item has bracket: { category_filed[id]: value }
            const regID = /\[[^\d]*(\d+)[^\d]*\]/.exec(item[0]);
            if (regID && regID.length > 1) {
                const id = regID[1];
                if (item[0].toString().includes('range')) {
                    if (Array.isArray(item[1]))
                        item[1].map((i) => ranges.push({ id: Number(id), value: i.split(',') }));
                    else ranges.push({ id: Number(id), value: item[1].split(',') });
                } else {
                    if (Array.isArray(item[1]))
                        item[1].map((i) => categoryFields.push({ id: Number(id), value: Number(i) }));
                    else categoryFields.push({ id: Number(id), value: Number(item[1]) });
                }
            }
        }
    });
    return { categoryFields, ranges };
};

export const __ConvertSearchToFilter = (
    e: __PostSearchParamsType,
    category?: CategoryType<'detail'>,
): __PostsFiltersType => {
    const { categoryFields, ranges } = extractListParams(e);
    return {
        page: e.page,
        search: e.search,
        categoryFields,
        ranges,
        category: category
            ? {
                  id: category.id,
                  title: category.title,
                  slug: category.slug,
                  image: category.image,
                  price: category.price,
                  parent: category.parent?.id,
                  renewed_price: category.renewed_price,
              }
            : undefined,
    };
};

export const __PostFetchServerURL = (e: __PostSearchParamsType, location?: LocationType<'list'>[]) => {
    const params = new URLSearchParams();
    params.append('page_size', '36');
    params.append('ordering', '-renewed_at');
    if (location) params.append('location_descendants__in', location.map((i) => i.id).toString());
    if (e.page) params.append('page', e.page.toString());
    if (e.search) params.append('title__icontains', e.search.toString());
    if (e.category) params.append('category_slug_descendants', e.category);
    const { categoryFields, ranges } = extractListParams(e);
    if (categoryFields?.length) categoryFields.map((f) => params.append(`category_field[${f.id}]`, f.value.toString()));
    if (ranges?.length) ranges.map((f) => params.append(`range_category_field[${f.id}]`, f.value.toString()));
    return API.posts.list + (params.toString().length ? '?' + params.toString() : '');
};

export const __PostFetchURL = (e: __PostsFiltersType, location?: LocationType<'list'>[]) => {
    const params = new URLSearchParams();
    params.append('page_size', '36');
    params.append('ordering', '-renewed_at');
    if (location) params.append('location_descendants__in', location.map((i) => i.id).toString());
    if (e.page) params.append('page', e.page.toString());
    if (e.search) params.append('title__icontains', e.search.toString());
    if (e.category) params.append('category_slug_descendants', e.category.slug);
    if (e.categoryFields?.length)
        e.categoryFields.map((f) => params.append(`category_field[${f.id}]`, f.value.toString()));
    if (e.ranges?.length) e.ranges.map((f) => params.append(`range_category_field[${f.id}]`, f.value.toString()));
    return API.posts.list + (params.toString().length ? '?' + params.toString() : '');
};
